export interface FirebaseAuthRequestConfig {
  firebaseAuthRetried?: boolean;
  headers?: {
    set?(name: string, value: string): void;
    [name: string]: unknown;
  };
  [name: string]: unknown;
}

interface FirebaseAccessTokenOptions {
  isWebView: boolean;
  fallbackToken?: string;
  requestWebViewToken(fallbackToken?: string, forceRefresh?: boolean): Promise<string | undefined>;
  getFirebaseToken(forceRefresh: boolean): Promise<string | undefined>;
}

interface FirebaseAuthRetryOptions<T> extends FirebaseAccessTokenOptions {
  status?: number;
  customToken?: string;
  requestConfig?: FirebaseAuthRequestConfig;
  retryRequest(config: FirebaseAuthRequestConfig): Promise<T>;
}

export const FIREBASE_AUTH_RETRY_NOT_ATTEMPTED = Symbol('firebase-auth-retry-not-attempted');

export const getFirebaseAccessToken = async ({
  isWebView,
  fallbackToken,
  requestWebViewToken,
  getFirebaseToken,
}: FirebaseAccessTokenOptions): Promise<string> => {
  if (isWebView && fallbackToken) {
    const currentToken = await requestWebViewToken(fallbackToken, false);

    return currentToken ?? fallbackToken;
  }

  try {
    const currentToken = await getFirebaseToken(false);

    if (currentToken) {
      return currentToken;
    }
  } catch {
    // Fall back to the Redux token during short Firebase bootstrap windows.
  }

  return fallbackToken ?? '';
};

const setFirebaseAuthorization = (requestConfig: FirebaseAuthRequestConfig, token: string) => {
  requestConfig.headers = requestConfig.headers ?? {};

  if (typeof requestConfig.headers.set === 'function') {
    requestConfig.headers.set('authorization', `Firebase ${token}`);
  } else {
    requestConfig.headers.authorization = `Firebase ${token}`;
  }
};

export const retryFirebaseAuthenticatedRequest = async <T>({
  status,
  customToken,
  requestConfig,
  isWebView,
  fallbackToken,
  requestWebViewToken,
  getFirebaseToken,
  retryRequest,
}: FirebaseAuthRetryOptions<T>): Promise<T | typeof FIREBASE_AUTH_RETRY_NOT_ATTEMPTED> => {
  if (status !== 401 || customToken || !requestConfig || requestConfig.firebaseAuthRetried) {
    return FIREBASE_AUTH_RETRY_NOT_ATTEMPTED;
  }

  let refreshedToken: string | undefined;

  try {
    refreshedToken = isWebView
      ? await requestWebViewToken(fallbackToken, true)
      : await getFirebaseToken(true);
  } catch {
    refreshedToken = undefined;
  }

  if (!refreshedToken) {
    return FIREBASE_AUTH_RETRY_NOT_ATTEMPTED;
  }

  requestConfig.firebaseAuthRetried = true;
  setFirebaseAuthorization(requestConfig, refreshedToken);

  return retryRequest(requestConfig);
};
